import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { DeliveryTypeDef } from '@api/types/DeliveryTypeDef';
import { PaymentTypeDef } from '@api/types/PaymentTypeDef';
import { DispatchPoint } from '@api/types/DispatchPoint';
import { invalidFormValue, isValidEmail, isValidPhoneNumber, isSpecificDelivery } from '@components/utils';
import { AppLanguageCode } from '@project-types/AppLanguageCodes';



export const selectOrderState = (state: RootState) => state.order;
export const selectShowInvalids = (state: RootState) => state.order.showInvalids;

export const selectSizeA = (state: RootState) => state.order.sizeA;
export const selectSizeB = (state: RootState) => state.order.sizeB;
export const selectSizeC = (state: RootState) => state.order.sizeC;
export const selectRace = (state: RootState) => state.order.race;
export const selectAge = (state: RootState) => state.order.age;
export const selectBehaviourDesc = (state: RootState) => state.order.behaviourDesc;
export const selectSurgerySegments = (state: RootState) => state.order.surgerySegments;
export const selectIssueDesc = (state: RootState) => state.order.issueDesc;
export const selectSurgeryDate = (state: RootState) => state.order.surgeryDate;
export const selectName = (state: RootState) => state.order.name;
export const selectEmail = (state: RootState) => state.order.email;
export const selectPhoneNumber = (state: RootState) => state.order.phoneNumber;

export const selectStreet = (state: RootState) => state.order.street;
export const selectCity = (state: RootState) => state.order.city;
export const selectPostalCode = (state: RootState) => state.order.postalCode;
export const selectInvoiceStreet = (state: RootState) => state.order.invoiceStreet;
export const selectInvoiceCity = (state: RootState) => state.order.invoiceCity;
export const selectInvoicePostalCode = (state: RootState) => state.order.invoicePostalCode;

export const selectSelectedType = (state: RootState) => state.order.selectedType;
export const selectCollarType = (state: RootState) => state.order.collarType;
export const selectMessage = (state: RootState) => state.order.message;
export const selectPayment = (state: RootState) => state.order.payment;
export const selectDelivery = (state: RootState) => state.order.delivery;
export const selectDispatchPoint = (state: RootState) => state.order.dispatchPoint;
export const selectDiscountCode = (state: RootState) => state.order.discountCode;
export const selectImageBase64 = (state: RootState) => state.order.imageBase64;
export const selectCountry = (state: RootState) => state.order.country;
export const selectSourceRef = (state: RootState) => state.order.sourceRef;



export const selectIsValid = createSelector(
	[
		selectSizeA,
		selectSizeB,
		selectSizeC,
		selectRace,
		selectAge,
		selectBehaviourDesc,
		selectSurgerySegments,
		selectSurgeryDate,
		selectName,
		selectEmail,
		selectPhoneNumber,
		selectStreet,
		selectCity,
		selectPostalCode,
		selectInvoiceStreet,
		selectInvoiceCity,
		selectInvoicePostalCode,
		selectSelectedType,
		selectCollarType,
		selectPayment,
		selectDelivery,
		selectDispatchPoint,
		selectCountry,
	],(
		sizeA: number | null,
		sizeB: number | null,
		sizeC: number | null,
		race: string | null,
		age: number | null,
		behaviourDesc: string | null,
		surgerySegments: string[],
		surgeryDate: string | null,
		name: string | null,
		email: string | null,
		phoneNumber: string | null,
		street: string | null,
		city: string | null,
		postalCode: string | null,
		invoiceStreet: string | null,
		invoiceCity: string | null,
		invoicePostalCode: string | null,
		selectedType: number | null,
		collarType: string | null,
		payment: PaymentTypeDef | null,
		delivery: DeliveryTypeDef | null,
		dispatchPoint: DispatchPoint | null,
		country: AppLanguageCode,
	) => {
		if (invalidFormValue(sizeA)) return false;
		if (invalidFormValue(sizeB)) return false;
		if (invalidFormValue(sizeC)) return false;
		if (invalidFormValue(race)) return false;
		if (invalidFormValue(age)) return false;
		if (invalidFormValue(behaviourDesc)) return false;
		if (!surgerySegments || surgerySegments.length === 0) return false;
		if (invalidFormValue(surgeryDate)) return false;
		if (invalidFormValue(name)) return false;
		if (invalidFormValue(email) || !isValidEmail(email)) return false;
		if (invalidFormValue(phoneNumber) || !isValidPhoneNumber(phoneNumber)) return false;
		if (invalidFormValue(invoiceStreet)) return false;
		if (invalidFormValue(invoiceCity)) return false;
		if (invalidFormValue(invoicePostalCode)) return false;
		if (invalidFormValue(selectedType)) return false;
		if (invalidFormValue(collarType)) return false;
		if (invalidFormValue(payment)) return false;
		if (invalidFormValue(delivery)) return false;
		if (invalidFormValue(country)) return false;
		if (street || city || postalCode) {
			if (invalidFormValue(street)) return false;
			if (invalidFormValue(city)) return false;
			if (invalidFormValue(postalCode)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.ZASILKOVNA_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.DPD_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.PPL_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		return true;
});



export const selectIsValidCat = createSelector(
	[
		selectSizeB,
		selectSizeC,
		selectRace,
		selectAge,
		selectBehaviourDesc,
		selectIssueDesc,
		selectSurgeryDate,
		selectName,
		selectEmail,
		selectPhoneNumber,
		selectStreet,
		selectCity,
		selectPostalCode,
		selectInvoiceStreet,
		selectInvoiceCity,
		selectInvoicePostalCode,
		selectSelectedType,
		selectPayment,
		selectDelivery,
		selectDispatchPoint,
		selectCountry,
	],(
		sizeB: number | null,
		sizeC: number | null,
		race: string | null,
		age: number | null,
		behaviourDesc: string | null,
		issueDesc: string | null,
		surgeryDate: string | null,
		name: string | null,
		email: string | null,
		phoneNumber: string | null,
		street: string | null,
		city: string | null,
		postalCode: string | null,
		invoiceStreet: string | null,
		invoiceCity: string | null,
		invoicePostalCode: string | null,
		selectedType: number | null,
		payment: PaymentTypeDef | null,
		delivery: DeliveryTypeDef | null,
		dispatchPoint: DispatchPoint | null,
		country: AppLanguageCode,
	) => {
		if (invalidFormValue(sizeB)) return false;
		if (invalidFormValue(sizeC)) return false;
		if (invalidFormValue(race)) return false;
		if (invalidFormValue(age)) return false;
		if (invalidFormValue(behaviourDesc)) return false;
		if (invalidFormValue(issueDesc)) return false;
		if (invalidFormValue(surgeryDate)) return false;
		if (invalidFormValue(name)) return false;
		if (invalidFormValue(email) || !isValidEmail(email)) return false;
		if (invalidFormValue(phoneNumber) || !isValidPhoneNumber(phoneNumber)) return false;
		if (invalidFormValue(invoiceStreet)) return false;
		if (invalidFormValue(invoiceCity)) return false;
		if (invalidFormValue(invoicePostalCode)) return false;
		if (invalidFormValue(selectedType)) return false;
		if (invalidFormValue(payment)) return false;
		if (invalidFormValue(delivery)) return false;
		if (invalidFormValue(country)) return false;
		if (street || city || postalCode) {
			if (invalidFormValue(street)) return false;
			if (invalidFormValue(city)) return false;
			if (invalidFormValue(postalCode)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.ZASILKOVNA_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.DPD_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		if (delivery && isSpecificDelivery(delivery.id, process.env.PPL_ID)) {
			if (invalidFormValue(dispatchPoint)) return false;
		}
		return true;
});